import * as THREE from 'three';
import {
  TREE_ROW_SPACING,
  TREE_DEFAULT_HEIGHT,
  TREE_TRUNK_COLOR,
  TREE_FOLIAGE_DECIDUOUS,
  TREE_FOLIAGE_CONIFER,
  TREE_FOLIAGE_DEFAULT,
} from '../constants.js';

// Species strings that indicate conifers
const CONIFER_KEYWORDS = ['pinus', 'picea', 'abies', 'larix', 'cupressus', 'thuja',
  'pine', 'spruce', 'fir', 'larch', 'cypress', 'cedar', 'juniper'];

/**
 * Build instanced tree meshes from individual tree nodes and tree-row ways.
 *
 * @param {Array}    trees     Parsed tree node objects: { id, lat, lon, height, leafType }
 * @param {Array}    treeRows  Parsed tree_row way objects: { id, nodes }
 * @param {Object}   proj      Projection: project(lat, lon) → {x, z}
 * @param {Function} elevFn    elevFn(x, z) → metres above scene centre
 * @returns {THREE.Group}
 */
export function buildTreeMesh(trees, treeRows, proj, elevFn = null) {
  // 1. Collect all tree positions + metadata
  const instances = [];

  for (const t of trees) {
    const p = proj.project(t.lat, t.lon);
    if (!isFinite(p.x) || !isFinite(p.z)) continue;
    instances.push({
      x:        p.x,
      z:        p.z,
      height:   t.height || TREE_DEFAULT_HEIGHT,
      conifer:  _isConifer(t.leafType, null),
      seed:     t.id,
    });
  }

  for (const row of treeRows) {
    const rowPts = _interpolateRow(row.nodes, proj, TREE_ROW_SPACING);
    for (let i = 0; i < rowPts.length; i++) {
      const p = rowPts[i];
      instances.push({
        x:       p.x,
        z:       p.z,
        height:  TREE_DEFAULT_HEIGHT,
        conifer: false,
        seed:    (row.id * 31 + i) | 0,
      });
    }
  }

  if (instances.length === 0) return new THREE.Group();

  // 2. Geometry templates
  const trunkGeo   = new THREE.CylinderGeometry(0.12, 0.2, 1, 6);
  const foliageGeo = new THREE.ConeGeometry(1.4, 3.5, 7);

  // 3. Two materials (deciduous + conifer); InstancedMesh supports per-instance colour
  const trunkMat     = new THREE.MeshLambertMaterial({ color: TREE_TRUNK_COLOR });
  const decidMat     = new THREE.MeshLambertMaterial({ color: TREE_FOLIAGE_DEFAULT });
  const coniferMat   = new THREE.MeshLambertMaterial({ color: TREE_FOLIAGE_CONIFER });

  // Split into deciduous and conifer buckets for two InstancedMesh objects
  const decid   = instances.filter(t => !t.conifer);
  const conifer = instances.filter(t =>  t.conifer);

  const group = new THREE.Group();
  _addInstancedTrees(group, decid,   trunkGeo, foliageGeo, trunkMat, decidMat,   elevFn);
  _addInstancedTrees(group, conifer, trunkGeo, foliageGeo, trunkMat, coniferMat, elevFn);

  return group;
}

// ── Internal helpers ──────────────────────────────────────────────────────────

function _addInstancedTrees(group, instances, trunkGeo, foliageGeo, trunkMat, foliageMat, elevFn) {
  const n = instances.length;
  if (n === 0) return;

  const trunkMesh   = new THREE.InstancedMesh(trunkGeo,   trunkMat,   n);
  const foliageMesh = new THREE.InstancedMesh(foliageGeo, foliageMat, n);
  trunkMesh.castShadow = foliageMesh.castShadow = true;

  const dummy = new THREE.Object3D();

  for (let i = 0; i < n; i++) {
    const t = instances[i];
    // ±20% height jitter from seed
    const jitter = 1 + ((t.seed % 7) - 3) * 0.065;
    const h      = t.height * jitter;
    const trunkH = Math.max(0.8, h * 0.22);
    const foliH  = h * 0.78;
    const y0     = elevFn ? elevFn(t.x, t.z) : 0;

    // Trunk: centred vertically at trunkH/2 above ground
    dummy.position.set(t.x, y0 + trunkH * 0.5, t.z);
    dummy.scale.set(1, trunkH, 1);
    dummy.updateMatrix();
    trunkMesh.setMatrixAt(i, dummy.matrix);

    // Foliage: base sits at top of trunk
    dummy.position.set(t.x, y0 + trunkH + foliH * 0.45, t.z);
    dummy.scale.set(1, foliH / 3.5, 1); // 3.5 = base cone height
    dummy.updateMatrix();
    foliageMesh.setMatrixAt(i, dummy.matrix);
  }

  trunkMesh.instanceMatrix.needsUpdate   = true;
  foliageMesh.instanceMatrix.needsUpdate = true;
  group.add(trunkMesh);
  group.add(foliageMesh);
}

/**
 * Interpolate evenly-spaced positions along a way polyline.
 */
function _interpolateRow(nodes, proj, spacing) {
  const pts = [];
  for (const n of nodes) {
    if (n.lat === undefined) continue;
    const p = proj.project(n.lat, n.lon);
    if (isFinite(p.x) && isFinite(p.z)) pts.push(p);
  }

  const result = [];
  if (pts.length < 2) return result;

  let accumulated = 0;

  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i], b = pts[i + 1];
    const dx = b.x - a.x, dz = b.z - a.z;
    const segLen = Math.sqrt(dx * dx + dz * dz);
    if (segLen < 0.01) continue;

    let dist = accumulated === 0 ? 0 : spacing - accumulated;
    while (dist <= segLen) {
      const t = dist / segLen;
      result.push({ x: a.x + dx * t, z: a.z + dz * t });
      dist += spacing;
    }
    accumulated = segLen - (dist - spacing);
  }

  return result;
}

function _isConifer(leafType) {
  if (leafType === 'needleleaved') return true;
  if (leafType === 'broadleaved')  return false;
  return false;
}
